import { scaleLinear } from 'd3-scale'
import { fixedDecimals } from '../../../platform/fixedDecimals'
import { isOver, shownPct } from '../budgetRules'
import type { Envelope } from '../budgetWire'
import '../budget.css'

/**
 * Envelope shape (B1): each active envelope is an axis, the dashed ring is the
 * plan (100%), and a vertex outside it is over. Hand-rolled SVG on `d3-scale`
 * (rule 3); skins are classes in `budget.css`.
 *
 * P3: 3–8 axes — the first 8 envelopes in list order; fewer than 3 is a
 * sentence, not a chart. A point is capped at 150% of the plan radius so one
 * 400% envelope cannot collapse the rest to the centre. Radii come from the
 * server's percentage; nothing here reads money.
 */

export const MAX_AXES = 8
const MIN_AXES = 3
const R = 90
const MAX_NAME = 14
const LABEL_GAP = 12
/** Caption text at --type-caption, in user units: a generous per-character width. */
const CHAR_W = 7
// The plot is wider than it is tall: the side labels need the room, the top and bottom need a line.
const W = 2 * (R + LABEL_GAP + MAX_NAME * CHAR_W)
const H = 2 * (R + LABEL_GAP + 14)
const CX = W / 2
const CY = H / 2

const num = (n: number) => fixedDecimals(n, 2)

function clip(name: string): string {
  return name.length > MAX_NAME ? `${name.slice(0, MAX_NAME - 1)}…` : name
}

function caption(over: readonly Envelope[]): string {
  const names = over.map((e) => e.name)
  if (names.length === 0) return 'Every envelope is inside its plan.'
  if (names.length === 1) return `Only ${names[0]} breaks the plan ring.`
  const head = names.slice(0, -1).join(', ')
  return `${head} and ${names[names.length - 1]} break the plan ring.`
}

type Props = { envelopes: Envelope[]; labelledBy: string }

export function EnvelopeRadar({ envelopes, labelledBy }: Props) {
  if (envelopes.length < MIN_AXES) {
    return <p className="radar-note">The shape needs three or more envelopes.</p>
  }
  const shown = envelopes.slice(0, MAX_AXES)
  const n = shown.length
  const radius = scaleLinear().domain([0, 150]).range([0, R]).clamp(true)
  const angle = (i: number) => (i * 2 * Math.PI) / n - Math.PI / 2
  const at = (i: number, pct: number) => {
    const r = radius(pct)
    return `${num(CX + r * Math.cos(angle(i)))},${num(CY + r * Math.sin(angle(i)))}`
  }
  const ring = (pct: number) => shown.map((_, i) => at(i, pct)).join(' ')

  return (
    <div className="radar">
      <svg
        className="radar-plot"
        role="img"
        aria-labelledby={labelledBy}
        viewBox={`0 0 ${W} ${H}`}
      >
        <polygon className="radar-grid" points={ring(50)} />
        <polygon className="radar-grid" points={ring(150)} />
        {shown.map((e, i) => (
          <line
            key={`axis-${e.id}`}
            className="radar-axis"
            x1={num(CX)}
            y1={num(CY)}
            x2={at(i, 150).split(',')[0]}
            y2={at(i, 150).split(',')[1]}
          />
        ))}
        <polygon className="radar-used" points={shown.map((e, i) => at(i, e.progress.percentage)).join(' ')} />
        <polygon className="radar-plan" points={ring(100)} />
        {shown.map((e, i) => {
          const cos = Math.cos(angle(i))
          const lx = CX + (R + LABEL_GAP) * cos
          const ly = CY + (R + LABEL_GAP) * Math.sin(angle(i))
          const anchor = Math.abs(cos) < 0.2 ? 'middle' : cos > 0 ? 'start' : 'end'
          return (
            <text
              key={`label-${e.id}`}
              className="radar-label"
              x={num(lx)}
              y={num(ly)}
              textAnchor={anchor}
              dominantBaseline="middle"
            >
              {clip(e.name)}
            </text>
          )
        })}
      </svg>
      <ul className="sr-only">
        {shown.map((e) => (
          <li key={e.id}>{`${e.name}: ${shownPct(e)}% of plan`}</li>
        ))}
      </ul>
      <p className="radar-caption">{caption(shown.filter(isOver))}</p>
      {envelopes.length > MAX_AXES && (
        <p className="radar-caption">{`Showing ${MAX_AXES} of ${envelopes.length} envelopes`}</p>
      )}
    </div>
  )
}
